import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildUsersQuery } from "@west-tennessee-consulting/supabase";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import type { User } from "@/lib/types/user";
import { getSupabase } from "../../utils";

export function useAdminUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean | null;
  userType?: string | null;
}) {
  return useQuery({
    queryKey: ["admin", "users", params],
    queryFn: async () => {
      const supabase = getSupabase();

      const query = buildUsersQuery(supabase, {
        isActive: params?.isActive,
        userType: params?.userType,
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
      });

      const { data, error } = await query;

      if (error) {
        logger.error("Error fetching admin users", error);
        throw new Error(error.message);
      }

      return data || [];
    },
    // No polling: every user mutation below invalidates ["admin", "users"],
    // and the default refetch-on-focus still picks up other admins' changes.
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: Partial<User>) => {
      const supabase = getSupabase();
      const { data: existingUser } = await supabase
        .from("app_users")
        .select("id")
        .eq("email", userData.email ?? "")
        .single();

      if (existingUser) {
        throw new Error("User with this email already exists");
      }

      const processedUserData = { ...userData };
      if (userData.role && !userData.role_id) {
        const { data: roleData, error: roleError } = await supabase
          .from("roles")
          .select("id")
          .eq("name", userData.role)
          .single();

        if (roleError) {
          throw new Error(`Invalid role: ${userData.role}`);
        }

        processedUserData.role_id = roleData.id;
        delete processedUserData.role;
      }

      const { data: newUser, error } = await supabase
        .from("app_users")
        .insert({
          ...processedUserData,
          is_active: processedUserData.is_active !== undefined ? processedUserData.is_active : true,
        })
        .select(
          `
          *,
          roles:role_id (
            id,
            name,
            display_name,
            description,
            permissions,
            is_active
          )
        `
        )
        .single();

      if (error) {
        logger.error("Error creating admin user", error);
        throw new Error("Failed to create user");
      }

      return newUser;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("Admin user created successfully");
    },
    onError: (error) => {
      toast.error("Failed to create admin user");
      logger.error("Error creating admin user", error);
    },
  });
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<User> }) => {
      const supabase = getSupabase();
      const { data: existingUser, error: fetchError } = await supabase
        .from("app_users")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError || !existingUser) {
        throw new Error("User not found");
      }

      const { data: updatedUser, error: updateError } = await supabase
        .from("app_users")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (updateError) {
        logger.error("Error updating admin user", updateError);
        throw new Error("Failed to update user");
      }

      return updatedUser;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("Admin user updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update admin user");
      logger.error("Error updating admin user", error);
    },
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      // Existence + both dependency checks are independent reads, so run them
      // together; the counts are head-only (no rows shipped) and the existence
      // check selects only the key. Checked in the original order so the
      // error a caller sees is unchanged.
      const [existingResult, ticketResult, timeEntryResult] = await Promise.all([
        supabase.from("app_users").select("id").eq("id", id).single(),
        supabase
          .from("tickets")
          .select("id", { count: "exact", head: true })
          .eq("assigned_to", id),
        supabase
          .from("time_entries")
          .select("id", { count: "exact", head: true })
          .eq("user_id", id),
      ]);

      if (existingResult.error || !existingResult.data) {
        throw new Error("User not found");
      }

      if ((ticketResult.count ?? 0) > 0) {
        throw new Error("Cannot delete user with assigned tickets");
      }

      if ((timeEntryResult.count ?? 0) > 0) {
        throw new Error("Cannot delete user with time entries");
      }

      const { error: deleteError } = await supabase.from("app_users").delete().eq("id", id);

      if (deleteError) {
        logger.error("Error deleting admin user", deleteError);
        throw new Error("Failed to delete user");
      }

      return { id };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("Admin user deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete admin user");
      logger.error("Error deleting admin user", error);
    },
  });
}
